import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Printer, Flag, CheckCircle, ArrowsLeftRight, XCircle } from "@phosphor-icons/react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");

export default function ProductionTimeline() {
  const { id } = useParams();
  const [run, setRun] = useState(undefined); // undefined = loading, null = not found

  useEffect(() => {
    api.get(`/production/${id}`).then((r) => setRun(r.data)).catch(() => setRun(null));
  }, [id]);

  const buildEvents = (r) => {
    const events = [
      { type: "start", at: r.created_at, by: r.by_name, label: `Order started — planned ${r.initial_quantity ?? r.quantity} × ${r.model}`, bom: r.bom_name },
    ];
    (r.completions || []).forEach((c) => {
      if (c.adjustment) {
        events.push({
          type: "adjust", at: c.at, by: c.by,
          label: `Plan changed ${c.quantity_change > 0 ? "+" : ""}${c.quantity_change} → ${c.new_planned} planned`,
        });
      } else {
        events.push({ type: "made", at: c.at, by: c.by, label: `${c.quantity} × ${r.model} recorded as made` });
      }
    });
    if (r.status === "cancelled" && r.cancelled_at) {
      events.push({ type: "cancel", at: r.cancelled_at, by: r.cancelled_by, label: "Order cancelled — remaining block released" });
    }
    return events.sort((a, b) => new Date(a.at) - new Date(b.at));
  };

  const iconFor = (t) => {
    if (t === "start") return <Flag size={16} weight="fill" className="text-zinc-800" />;
    if (t === "made") return <CheckCircle size={16} weight="fill" className="text-[#16A34A]" />;
    if (t === "adjust") return <ArrowsLeftRight size={16} weight="bold" className="text-[#EA580C]" />;
    return <XCircle size={16} weight="fill" className="text-[#DC2626]" />;
  };

  return (
    <>
      <PageHeader
        title={run ? `${run.order_no || "Order"} Timeline` : "Order Timeline"}
        subtitle={run ? `${run.model} Oven · ${run.made || 0} / ${run.quantity} made · ${run.status}` : run === null ? "Not found" : "Loading…"}
      >
        {run && (
          <Button onClick={() => window.print()} data-testid="print-timeline" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider text-xs print:hidden">
            <Printer size={16} weight="bold" className="mr-1" /> Print
          </Button>
        )}
      </PageHeader>
      <div className="p-4 sm:p-8">
        {run === undefined ? (
          <p className="text-sm text-zinc-400">Loading…</p>
        ) : run === null ? (
          <p className="text-sm text-zinc-400" data-testid="timeline-not-found">This production order no longer exists.</p>
        ) : (
          <div className="bg-white border border-zinc-300 max-w-3xl">
            {/* Summary header */}
            <div className="px-6 py-5 border-b border-zinc-300 bg-zinc-50">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono text-sm font-bold bg-zinc-900 text-white px-2 py-1">{run.model}</span>
                <span className="font-mono text-sm font-bold text-primary">{run.order_no}</span>
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase text-white ${run.status === "completed" ? "bg-[#16A34A]" : run.status === "cancelled" ? "bg-zinc-400" : "bg-[#EA580C]"}`}>{run.status}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 text-sm">
                <div><p className="text-[11px] uppercase tracking-wider text-zinc-400">Planned</p><p className="font-mono font-bold text-lg">{run.quantity}</p></div>
                <div><p className="text-[11px] uppercase tracking-wider text-zinc-400">Made</p><p className="font-mono font-bold text-lg">{run.made || 0}</p></div>
                <div><p className="text-[11px] uppercase tracking-wider text-zinc-400">BOM</p><p className="text-sm">{run.bom_name || "—"}</p></div>
                <div><p className="text-[11px] uppercase tracking-wider text-zinc-400">Started by</p><p className="text-sm">{run.by_name}</p></div>
              </div>
            </div>
            {/* Timeline */}
            <div className="px-6 py-6" data-testid="timeline-events">
              <ol className="relative border-l border-zinc-200 ml-2">
                {buildEvents(run).map((e, i) => (
                  <li key={`${e.type}-${e.at}-${i}`} className="mb-6 ml-6 last:mb-0" data-testid={`timeline-event-${i}`}>
                    <span className="absolute -left-[9px] flex items-center justify-center w-[18px] h-[18px] bg-white">{iconFor(e.type)}</span>
                    <p className="text-sm font-medium text-zinc-900">{e.label}</p>
                    {e.bom && <p className="text-xs text-zinc-500">BOM: {e.bom}</p>}
                    <p className="text-[11px] text-zinc-400 mt-0.5">{fmt(e.at)} · {e.by}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
